import { Address, Data, GasData, IntentMessage, ObjectDigest, Owner, ProgrammableTransaction, TransactionExpiration, TypeTag, base64String } from "./bcs.mjs";
import { bcs } from "@safecoin/bcs";
//#region src/bcs/transactions.ts
const ChangeEpoch = bcs.struct("ChangeEpoch", {
	epoch: bcs.u64(),
	protocolVersion: bcs.u64(),
	storageCharge: bcs.u64(),
	computationCharge: bcs.u64(),
	storageRebate: bcs.u64(),
	nonRefundableStorageFee: bcs.u64(),
	epochStartTimestampMs: bcs.u64(),
	systemPackages: bcs.vector(bcs.tuple([
		bcs.u64(),
		bcs.vector(bcs.byteVector()),
		bcs.vector(Address)
	]))
});
const GenesisObject = bcs.enum("GenesisObject", { RawObject: bcs.struct("RawObject", {
	data: Data,
	owner: Owner
}) });
const GenesisTransaction = bcs.struct("GenesisTransaction", { objects: bcs.vector(GenesisObject) });
const ConsensusCommitPrologue = bcs.struct("ConsensusCommitPrologue", {
	epoch: bcs.u64(),
	round: bcs.u64(),
	commitTimestampMs: bcs.u64()
});
const ConsensusCommitPrologueV2 = bcs.struct("ConsensusCommitPrologueV2", {
	epoch: bcs.u64(),
	round: bcs.u64(),
	commitTimestampMs: bcs.u64(),
	consensusCommitDigest: ObjectDigest
});
const ConsensusDeterminedVersionAssignments = bcs.enum("ConsensusDeterminedVersionAssignments", {
	CancelledTransactions: bcs.vector(bcs.tuple([ObjectDigest, bcs.vector(bcs.tuple([Address, bcs.u64()]))])),
	CancelledTransactionsV2: bcs.vector(bcs.tuple([ObjectDigest, bcs.vector(bcs.tuple([bcs.tuple([Address, bcs.u64()]), bcs.u64()]))]))
});
const ConsensusCommitPrologueV3 = bcs.struct("ConsensusCommitPrologueV3", {
	epoch: bcs.u64(),
	round: bcs.u64(),
	subDagIndex: bcs.option(bcs.u64()),
	commitTimestampMs: bcs.u64(),
	consensusCommitDigest: ObjectDigest,
	consensusDeterminedVersionAssignments: ConsensusDeterminedVersionAssignments
});
const ConsensusCommitPrologueV4 = bcs.struct("ConsensusCommitPrologueV4", {
	epoch: bcs.u64(),
	round: bcs.u64(),
	subDagIndex: bcs.option(bcs.u64()),
	commitTimestampMs: bcs.u64(),
	consensusCommitDigest: ObjectDigest,
	consensusDeterminedVersionAssignments: ConsensusDeterminedVersionAssignments,
	additionalStateDigest: ObjectDigest
});
const ActiveJwk = bcs.struct("ActiveJwk", {
	jwkId: bcs.struct("JwkId", {
		iss: bcs.string(),
		kid: bcs.string()
	}),
	jwk: bcs.struct("JWK", {
		kty: bcs.string(),
		e: bcs.string(),
		n: bcs.string(),
		alg: bcs.string()
	}),
	epoch: bcs.u64()
});
const AuthenticatorStateUpdate = bcs.struct("AuthenticatorStateUpdate", {
	epoch: bcs.u64(),
	round: bcs.u64(),
	newActiveJwks: bcs.vector(ActiveJwk),
	authenticatorObjInitialSharedVersion: bcs.u64()
});
const RandomnessStateUpdate = bcs.struct("RandomnessStateUpdate", {
	epoch: bcs.u64(),
	randomnessRound: bcs.u64(),
	randomBytes: bcs.byteVector(),
	randomnessObjInitialSharedVersion: bcs.u64()
});
const AuthenticatorStateExpire = bcs.struct("AuthenticatorStateExpire", {
	minEpoch: bcs.u64(),
	authenticatorObjInitialSharedVersion: bcs.u64()
});
const ExecutionTimeObservationKey = bcs.enum("ExecutionTimeObservationKey", {
	MoveEntryPoint: bcs.struct("MoveEntryPoint", {
		package: Address,
		module: bcs.string(),
		function: bcs.string(),
		typeArguments: bcs.vector(TypeTag)
	}),
	TransferObjects: null,
	SplitCoins: null,
	MergeCoins: null,
	Publish: null,
	MakeMoveVec: null,
	Upgrade: null
});
const StoredExecutionTimeObservations = bcs.enum("StoredExecutionTimeObservations", { V1: bcs.vector(bcs.tuple([ExecutionTimeObservationKey, bcs.vector(bcs.tuple([bcs.byteVector(), bcs.struct("Duration", {
	secs: bcs.u64(),
	nanos: bcs.u32()
})]))])) });
const WriteAccumulatorStorageCost = bcs.struct("WriteAccumulatorStorageCost", { storageCost: bcs.u64() });
const EndOfEpochTransactionKind = bcs.enum("EndOfEpochTransactionKind", {
	ChangeEpoch,
	AuthenticatorStateCreate: null,
	AuthenticatorStateExpire,
	RandomnessStateCreate: null,
	DenyListStateCreate: null,
	BridgeStateCreate: ObjectDigest,
	BridgeCommitteeInit: bcs.u64(),
	StoreExecutionTimeObservations: StoredExecutionTimeObservations,
	AccumulatorRootCreate: null,
	CoinRegistryCreate: null,
	DisplayRegistryCreate: null,
	AddressAliasStateCreate: null,
	WriteAccumulatorStorageCost,
	ForwardingAddressRegistryCreate: null
});
const TransactionKind = bcs.enum("TransactionKind", {
	ProgrammableTransaction,
	ChangeEpoch,
	Genesis: GenesisTransaction,
	ConsensusCommitPrologue,
	AuthenticatorStateUpdate,
	EndOfEpochTransaction: bcs.vector(EndOfEpochTransactionKind),
	RandomnessStateUpdate,
	ConsensusCommitPrologueV2,
	ConsensusCommitPrologueV3,
	ConsensusCommitPrologueV4,
	ProgrammableSystemTransaction: ProgrammableTransaction
});
const TransactionDataV1 = bcs.struct("TransactionDataV1", {
	kind: TransactionKind,
	sender: Address,
	gasData: GasData,
	expiration: TransactionExpiration
});
const TransactionData = bcs.enum("TransactionData", { V1: TransactionDataV1 });
const SenderSignedTransaction = bcs.struct("SenderSignedTransaction", {
	intentMessage: IntentMessage(TransactionData),
	txSignatures: bcs.vector(base64String)
});
const SenderSignedData = bcs.vector(SenderSignedTransaction, { name: "SenderSignedData" });
//#endregion
export { ActiveJwk, AuthenticatorStateExpire, AuthenticatorStateUpdate, ChangeEpoch, ConsensusCommitPrologue, ConsensusCommitPrologueV2, ConsensusCommitPrologueV3, ConsensusCommitPrologueV4, ConsensusDeterminedVersionAssignments, EndOfEpochTransactionKind, ExecutionTimeObservationKey, GenesisObject, GenesisTransaction, RandomnessStateUpdate, SenderSignedData, SenderSignedTransaction, StoredExecutionTimeObservations, TransactionData, TransactionDataV1, TransactionKind, WriteAccumulatorStorageCost };

//# sourceMappingURL=transactions.mjs.map